/*
In general, this class is used to store sequence data. Contrast with components/Sequence which is more in
charge of rendering and UI concerns. In particular, this can come up when we consider features that aggregate
data from multiple sources, e.g. transcriptions. There is no MolBio data for transcriptions, as the data
storage is completely dependent on Schemas. For that reason, transcription data is not stored on this class,
but rather is fetched from components/Sequence.

 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS103: Rewrite code to no longer use __guard__
 * DS104: Avoid inline assignments
 * DS201: Simplify complex destructure assignments
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */

import {groupBy, groupByUniqueKey} from '@benchling/js-utils';
import Backbone, {ModelSetOptions, ObjectHash} from 'backbone';
import $ from 'jquery';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import * as AssemblyModels from 'assembly/AssemblyModels';
import {Assembly} from 'assembly/AssemblyModels';
import {assert, assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {sum} from 'BenchlingUtil';
import {PrimerPairCollection} from 'BioJS';
import * as SequenceEdits from 'biojs/SequenceEdits';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import {NumberIDFolder} from 'constants/FolderConstants';
import {
  CustomField,
  OPPOSITE_STRAND,
  OPPOSITE_STRAND_OF_DOUBLE_STRANDED,
  Side,
  Strand,
} from 'constants/MolbioConstants';
import {BioentityAliasInfo, TagSchema} from 'constants/RegistryConstants';
import {
  ModifiedNucleotideAttributes,
  PolymerFeatureAttributes,
  PolymerFeatureModel,
  SequenceServerResponse,
  SequenceType,
} from 'constants/SequenceConstants';
import {BulkSetModsParams, FrontendSeqOps, SeqOperation, SeqOpOptions} from 'constants/SequenceOpConstants';
import {User} from 'constants/UserConstants';
import * as context from 'Context';
import {Tag} from 'folder-items/FileConstants';
import {IsContainedInComplexPolymerData} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {reindexModifiedNucleotides} from 'modbio/util';
import {search, SequenceMatch, SearchOptions} from 'molbio/biological-search/Search';
import {getSingleCopiedPrimer} from 'molbio/CopyUtil';
import {Cut, DigestableEnzyme, Enzyme} from 'molbio/enzymes/EnzymeConstants';
import {getCuts} from 'molbio/enzymes/EnzymeUtil';
import * as sequtil from 'molbio/SeqUtil';
import {isCircularRangeWithin} from 'molbio/SeqUtil';
import {FromServerNote, NotePart} from 'notebook/util/NoteUtilConstants';
import {tagIsComputed} from 'schemas/fields/computed/ComputedFieldUtil';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import sortedBy from 'util/sortedBy';

import {AnnotationAttributes, Collection as Annotations, Model as Annotation} from './Annotation';
import {
  CODON_LENGTH,
  CodonTranslationTable,
  IupacPartialMapping,
  NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE,
  NucleotideType,
} from './BioConstants';
import * as FragmentResult from './FragmentResult';
import * as PartModels from './Part';
import {Part, Parts} from './Part';
import {Collection as PrimerPairs, Model as PrimerPair} from './PrimerPair';
import {
  Collection as SequencePrimers,
  Model as SequencePrimer,
  SequencePrimerAttributes,
} from './SequencePrimer';
import {Collection as Translations, Model as Translation, TranslationAttributes} from './Translation';
import * as util from './Util';

type Overhang = "5'" | "3'" | 'blunt';
export type Casing = 'upper' | 'lower';

interface SaveOptions extends SeqOpOptions {
  save?: boolean;
  wait?: boolean;
  renderClient?: () => void;
  noteParts?: Array<NotePart>;
}

export interface SaveXHR<ReturnT> extends JQueryXHR {
  client: JQueryPromise<ReturnT>;
}

function getRejectedSaveXHRForValidationFailure<ReturnT>(sequence: Sequence): SaveXHR<ReturnT> {
  const validationMessage = sequence.validate();
  // Sequence is in a bad state, so make sure we can track the stacktrace issue.
  assertAndContinue(false, `Validation error when trying to save sequence: ${validationMessage}`);
  const deferred = $.Deferred();
  // This is not a true JQueryXHR object, but it follows the same pattern we use in places like Backbone.sync
  // defined in backbone-extensions.
  const rejectedSaveXHR = deferred.promise() as SaveXHR<ReturnT>;
  rejectedSaveXHR.client = rejectedSaveXHR;
  deferred.reject({responseJSON: {devMessage: validationMessage}});
  return rejectedSaveXHR;
}

export interface TranslationGroup {
  id: number | string;
  name: string;
  notes: string;
  customFields: Array<CustomField>;
  codonTranslationTable: CodonTranslationTable;
  coerceStartCodonToMethionine: boolean;
  color: string;
  fromSubPart?: boolean;
}

// Can be either BioJSSequencePrimer or ComponentSequencePrimer
export interface BasePrimer {
  start(): number;
  end(): number;
  bases(): string;
  overhangBases(): string;
  id?: number | null;
  nucleotideType(): NucleotideType;
}

// Representing split annotations without an ID specified so that Backbone doesn't dedupe them.
type AnnotationAttributesWithOptionalID = Omit<AnnotationAttributes, 'id'> & {id?: number};
interface TranslationRegionGroupForCreate {
  groupId: number | null;
  regions: Array<TranslationAttributes>;
}

export interface CreateFromIndicesOptions {
  // If true, we carry over annotations, translations, and primers.
  annotations?: boolean;
  // If true, we carry over features that are only partially contained by the selection
  notcontained?: boolean;
  tags?: boolean;
  reverseComplement?: boolean;
  padStart?: string;
  padEnd?: string;
  // If specified, the primer pair to use for PCR product.
  primers?: [BasePrimer | null, BasePrimer | null];
  primerBases?: boolean;
  preserveIndices?: boolean;
  bases?: string;
  name?: string;
  // seemingly unused boolean
  save?: boolean;
  parentVersionId?: number;
  type?: SequenceType;
}

interface VersionSummary {
  name: string;
  id: number;
  parentId: number | null;
  created_at: string;
  user: User;
}

// TODO: consolidate with `AlignmentConstants`
export enum SequenceAnalysisType {
  DIGEST = 'digest',
  ALIGNMENT = 'alignment',
}

export interface SequenceAnalysisSummary {
  id: number;
  name: string;
  analysis_type: SequenceAnalysisType;
  created_at: string;
  sequence_id: string;
  apiId: string;
}

export type ModifiedNucleotides = Readonly<Array<Readonly<ModifiedNucleotideAttributes>>>;

export interface SequenceAttributes {
  '3'?: string;
  '5'?: string;
  _cutter3?: DigestableEnzyme;
  _cutter5?: DigestableEnzyme;
  _startIndex?: number;
  analyses: Array<SequenceAnalysisSummary>;
  annotations: Annotations;
  archiveRecord: ArchiveRecord | null;
  assembly?: Assembly;
  authors: Array<User>;
  bases: string;
  bioentityAliasInfos: Array<BioentityAliasInfo>;
  canOpenSelectionContextMenu: boolean;
  circular: boolean;
  color: string | null;
  created_at: string;
  creator: string;
  displayLabel: string;
  editURL: string;
  end?: number;
  finalized?: boolean;
  folder?: NumberIDFolder;
  fragmentResults: FragmentResult.Collection;
  id: string;
  isContainedInComplexPolymer: boolean;
  isContainedInComplexPolymerData: IsContainedInComplexPolymerData | null;
  latestVersionId: number;
  length: number;
  locked?: boolean;
  modified_at: string;
  modifiedNucleotides: ModifiedNucleotides;
  name: string;
  note: FromServerNote;
  nucleotideType: NucleotideType;
  offset5: number;
  offset3: number;
  parents?: unknown;
  parentVersionId?: number;
  parts: Parts;
  permissions: Permissions;
  primerPairs: PrimerPairs;
  projectFolderId?: string | null;
  schemaValidationState: SchemaValidationState | null;
  selected?: boolean;
  sequencePrimers: SequencePrimers;
  start?: number;
  startIndex: number;
  translationGroups: Array<TranslationGroup>;
  translations: Translations;
  tags?: Array<Tag>;
  tagSchema?: TagSchema;
  tagSchemaId?: string;
  type: SequenceType;
  unrecoverable: boolean;
  versions: Array<VersionSummary>;
  writable: boolean;
}

export interface Sequence {
  get<K extends keyof SequenceAttributes>(attribute: K): SequenceAttributes[K];
  set(obj: Partial<SequenceAttributes>, options?: ModelSetOptions): Sequence;
  set<K extends keyof SequenceAttributes>(
    attributeName: K,
    value: SequenceAttributes[K],
    options?: ModelSetOptions
  ): Sequence;
}
export class Sequence extends Backbone.Model {
  static initClass(): void {
    this.prototype.urlRoot = '/1/api/sequences';
  }

  defaults(): ObjectHash {
    return {
      bases: '',
      type: 'ds',
      name: 'Untitled',
      color: null, // for primers

      offset5: 0, // positive overhang of the top strand at the 5' end
      offset3: 0, // positive recess of the top strand at the 3' end

      circular: false,
      startIndex: 1,
      nucleotideType: NucleotideType.DNA,

      // If we want to use null's here, then everyone who uses
      // a non-benchling synced biojs.sequence must set these to
      // not null, e.g. EditPrimerTabState and Alignments.

      annotations: new Annotations(),
      translations: new Translations(),
      translationGroups: [],
      sequencePrimers: new SequencePrimers(),
      primerPairs: new PrimerPairs(),
      fragmentResults: new FragmentResult.Collection(),
      parts: new PartModels.Collection(),

      // If the user is signed out, we mark the sequence as unrecoverable. Even if they refresh their session
      // in another window, we don't want to save the work on the old version. If we properly implement
      // versioning for saves, we should remove this.
      unrecoverable: false,
      modifiedNucleotides: [],
      canOpenSelectionContextMenu: true,
    };
  }

  leftOverhang(): Overhang {
    if (this.get('offset5') > 0) {
      // 5   TAGGG...
      return "5'"; // 3     CCC...
    } else if (this.get('offset5') < 0) {
      // 5     CCC...
      return "3'"; // 3   TAGGG...
    } else {
      // 5   ATCCC...
      return 'blunt'; // 3   TAGGG...
    }
  }

  rightOverhang(): Overhang {
    if (this.get('offset3') > 0) {
      // ...CCC    3
      return "5'"; // ...GGGAT  5
    } else if (this.get('offset3') < 0) {
      // ...GGGAT  3
      return "3'"; // ...CCC    5
    } else {
      // ...GGGAT  3
      return 'blunt'; // ...CCCTA  5
    }
  }

  latestVersionId(): number {
    return this.get('latestVersionId');
  }

  setLatestVersionId(versionId: number): void {
    this.set({latestVersionId: versionId});
  }

  createdAt(): string {
    return this.get('created_at');
  }

  circular(): boolean {
    return this.get('circular');
  }
  startIndex(): number {
    return this.get('startIndex');
  }
  annotations(): Annotations {
    return this.get('annotations');
  }
  locked(): boolean | undefined {
    return this.get('locked');
  }

  translations(): Translations {
    return this.get('translations');
  }
  translationGroups(): Array<TranslationGroup> {
    return this.get('translationGroups');
  }
  sequencePrimers(): SequencePrimers {
    return this.get('sequencePrimers');
  }
  primerPairs(): PrimerPairs {
    return this.get('primerPairs');
  }
  modifiedNucleotides(): SequenceAttributes['modifiedNucleotides'] {
    return this.get('modifiedNucleotides') ?? [];
  }
  name(): string {
    return this.get('name');
  }
  displayLabel(): string {
    return this.get('displayLabel');
  }
  isArchived(): boolean {
    return Boolean(this.get('archiveRecord'));
  }
  folder(): NumberIDFolder | undefined {
    return this.get('folder');
  }
  parts(): Parts {
    return this.get('parts');
  }
  bases(): string {
    return this.get('bases');
  }

  nucleotideType(): NucleotideType {
    return this.get('nucleotideType');
  }

  length(): number {
    return this.bases().length;
  }
  finalized(): boolean | undefined {
    return this.get('finalized');
  }
  parents(): unknown {
    // TODO: remove once we confirm it's unused.
    assertAndContinue(false, 'Usage of code we thought was dead.');
    return this.get('parents');
  }
  fragmentResults(): FragmentResult.Collection {
    return this.get('fragmentResults');
  }
  assembly(): Assembly | undefined {
    return this.get('assembly');
  }
  assemblyType(): ASSEMBLY_TYPE | undefined {
    return __guard__(this.assembly(), (x: Assembly) => x.method() as ASSEMBLY_TYPE);
  }

  baseTable(): IupacPartialMapping {
    return NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[this.nucleotideType()];
  }

  canOpenSelectionContextMenu(): boolean {
    return this.get('canOpenSelectionContextMenu');
  }

  // TODO: return type should just be void
  _changeBases(): Sequence | void {
    if (!context.get('modifying')) {
      return this.triggerBaseChange();
    }
  }

  initialize(attrs: SequenceAttributes, options?: {}): void {
    this.annotations().add(this.parts().annotations(this));
    this.translations().add(this.parts().translations(this));
    this.sequencePrimers().add(this.parts().sequencePrimers(this));
    this.primerPairs().add(this.parts().primerPairs(this));
    this.set('translationGroups', [...this.translationGroups(), ...this.parts().translationGroups(this)]);

    // TODO is this ever used?
    if (this.has('3') && this.has('5')) {
      const blank = (char: string): boolean => char === undefined || char === ' ';

      let offset5 = 0;
      let offset3 = 0;
      let header = true;
      let bases = '';
      const len = Math.max(this.get('5')!.length, this.get('3')!.length);

      for (let i = 0; i < len; i++) {
        const top = this.get('5')![i];
        const bot = this.get('3')![i];

        if (blank(top) && blank(bot)) {
          // noop
        } else if (blank(top)) {
          bases += util.complement(bot, this.baseTable());
          if (header) {
            offset5 -= 1;
          } else {
            offset3 += 1;
          }
        } else if (blank(bot)) {
          bases += top;
          if (header) {
            offset5 += 1;
          } else {
            offset3 -= 1;
          }
        } else {
          assert(top === util.complement(bot, this.baseTable()), 'complementary base mismatch');
          header = false;
          bases += top;
        }
      }

      this.set({
        offset5,
        offset3,
        circular: false,
        type: SequenceType.DOUBLE_STRANDED,
      });
      this.set('bases', bases);

      this.unset('3');
      this.unset('5');
    }

    this._changeBases();

    this.on('change:parts', function (this: Sequence): void {
      // stop listening to the old parts collections
      const oldParts = this.previous('parts');
      if (oldParts != null) {
        this.stopListening(oldParts);
      }
      // start listening to the new parts collection
      this.listenToParts();
      this.parts().setLayers();

      this._updatePartFeatures();
    });

    this.listenToParts();
    this.on('change:type', function (this: Sequence): Sequence | void {
      return this._changeBases();
    });

    this.listenTo(
      this,
      'change:sequencePrimers',
      function (this: Sequence, model: Sequence, value: SequencePrimers): void {
        if (this.previous('sequencePrimers') != null) {
          this.stopListening(this.previous('sequencePrimers'));
        }
        return this.listenTo(value, 'remove', this._cleanPairs.bind(this));
      }
    );
  }

  // TODO(BNCH-25393): Investigate whether this is still needed.
  listenToParts(): void {
    return this.listenTo(this.parts(), 'change:bases', function (this: Sequence): Sequence | void {
      return this._changeBases();
    });
  }

  _cleanPairs(): Array<PrimerPair> {
    /* Removes pairs with primers that have been removed. */
    const primers = this.sequencePrimers();
    const pairs = this.primerPairs();
    const cleanedPairs = this.primerPairs().filter((pair) => {
      const forward = primers.find((primer) => primer.id === pair.forwardPrimerId());
      const reverse = primers.find((primer) => primer.id === pair.reversePrimerId());
      return forward != null && reverse != null;
    });
    return pairs.reset(cleanedPairs);
  }

  at(pos: number): string {
    return this.bases()[pos];
  }

  getSequencePartsAt(position: number): Array<Part> {
    assert(this.length() === 0 || (0 <= position && position < this.length()), 'Unexpected position', {
      position,
      length: this.length(),
    });
    return this.parts().getSequencePartsAt(position);
  }

  partHasOverlaps(part: Part): boolean {
    return this.parts().partHasOverlaps(part);
  }

  hasNullPhosphate(): boolean {
    return this.modifiedNucleotides().some((modifiedNucleotide) => !modifiedNucleotide.phosphateMonomerId);
  }

  tagSchemaHasTranscriptionLinkFields(): boolean {
    const transcriptionFields = this.get('tagSchema')?.allFields.filter(
      (field) => field.fieldType === FieldType.TRANSCRIPTION_LINK
    );
    return transcriptionFields ? transcriptionFields.length > 0 : false;
  }

  search(seq: string, options?: Partial<SearchOptions>): Array<SequenceMatch> {
    return search(seq, this.bases(), this.nucleotideType(), this.get('type'), this.circular(), options);
  }

  offsets(): [number, number] {
    /* simple convenience method */
    return [this.get('offset5'), this.get('offset3')];
  }

  cutters(): [DigestableEnzyme | undefined, DigestableEnzyme | undefined] {
    /* simple convenience method */
    return [this.get('_cutter5'), this.get('_cutter3')];
  }

  overhang5(): string {
    return this.bases().slice(0, Math.abs(this.get('offset5')));
  }

  overhang3(): string {
    return this.bases().slice(this.length() - Math.abs(this.get('offset3')), this.length());
  }

  topStrand(): string {
    // gets the "top strand" as denoted by our own representation
    // don't forget that the actual molecule is directionless

    const left = -Math.min(0, this.get('offset5'));
    const right = this.length() - Math.max(0, this.get('offset3'));

    return this.bases().slice(left, right);
  }

  getCuts(enzyme: DigestableEnzyme, range: {start: number; end: number} | null = null): Array<Cut> {
    return getCuts(enzyme, this.bases(), this.nucleotideType(), this.get('type'), this.circular(), range);
  }

  enzymeOverlap(
    enzymes: Array<DigestableEnzyme>
  ): Array<{start: number; end: number; enzymes: Array<string>}> {
    function makeEnzymeEvents(this: Sequence, e: DigestableEnzyme): Array<[Side, string, number]> {
      // @ts-expect-error TODO fix types here
      return _.chain(this.search(e.matchingRegion))
        .map((m, i) => {
          const left = m.index;
          const right = m.index + e.matchingRegion.length;
          return [
            ['start', `${e.name}_${i}`, left],
            ['end', `${e.name}_${i}`, right],
          ];
        }, this)
        .flatten(true)
        .value();
    }

    // eslint-disable-next-line benchlint/no-function-ref-in-array-method
    const events = _.chain(enzymes)
      .map(makeEnzymeEvents, this)
      .flatten(true)
      .sortBy((r) => r[0]) // radix sort, sort all features, but ensure that ends come
      .sortBy((r) => r[2]) // before starts.
      .value();

    let currentOverlap: Array<string> = [];

    let currentStart = 0;
    let currentEnd = 0;

    let lastEvent = null;
    const overlaps: Array<{start: number; end: number; enzymes: Array<string>}> = [];
    for (const r of Array.from(events)) {
      if (r[0] === 'start') {
        currentOverlap.push(r[1]);
        currentStart = r[2];
      } else if (r[0] === 'end') {
        currentEnd = r[2];
        if (currentOverlap.length > 1 && lastEvent !== 'end') {
          overlaps.push({
            start: currentStart % this.length(),
            end: currentEnd % this.length(),
            enzymes: currentOverlap.map((_r) => _r.slice(0, _r.lastIndexOf('_'))),
          });
        }
        currentOverlap = currentOverlap.filter((overlap) => overlap !== r[1]);
      }
      lastEvent = r[0];
    }

    return overlaps;
  }

  digest(enzyme: Enzyme): SequenceCollection {
    let left;
    let right;
    assert(this.get('type') === 'ds', 'digest can only be performed on ds dna');

    const fragments = new SequenceCollection();

    // 0. If we have an empty sequence, it has no fragments.
    if (this.length() === 0) {
      return fragments;
    }

    // 1. Compute matches for enzyme
    if (!enzyme.hasOwnProperty('matchingRegion')) {
      // Adds digestable keys to enzyme
      util.digestableEnzyme(enzyme);
    }
    const digestableEnzyme: DigestableEnzyme = enzyme as DigestableEnzyme;

    const matches = this.search(digestableEnzyme.matchingRegion);

    // 2. Normalize the matches into {left, right, offset} for each cut (including cut2)
    let cuts: Array<{left: number; right: number; offset: number}> = [];
    const cutAndOffset = [[digestableEnzyme.matchingCut, digestableEnzyme.offset]];
    if (digestableEnzyme.cut2 != null) {
      // If cut2 is populated, assume offset2 is also populated
      cutAndOffset.push([digestableEnzyme.matchingCut2!, digestableEnzyme.offset2!]);
    }
    for (const [matchingCut, offset] of Array.from(cutAndOffset)) {
      for (const m of Array.from(matches)) {
        [left, right] = Array.from(
          _.chain([matchingCut, matchingCut + offset])
            .map((d) => {
              if (m.strand === -1) {
                return digestableEnzyme.matchingRegion.length - d;
              } else {
                return d;
              }
            })
            .sortBy((d) => d)
            .map((d) => sequtil.mod(m.index + d, this.length()))
            .value()
        );
        cuts.push({left, right, offset});
      }
    }
    cuts = _.sortBy(cuts, (cut) => cut.left);

    const left1 = this.get('_startIndex');
    const startIndex = left1 != null ? left1 : 0;
    const addFragment = (
      _left: number,
      _right: number,
      offset5: number,
      offset3: number,
      cutter5: DigestableEnzyme,
      cutter3: DigestableEnzyme
    ): void => {
      fragments.add(
        this.slice(_left, _right).set({
          offset5,
          offset3,
          type: SequenceType.DOUBLE_STRANDED,
          circular: false,
          _startIndex: startIndex + _left,
          _cutter5: cutter5,
          _cutter3: cutter3,
        })
      );
    };

    // 3. No cuts => the original sequence stays in tact
    if (cuts.length === 0) {
      const fragment = this.clone();
      fragment.set({
        _startIndex: startIndex,
        _cutter5: this.get('_cutter5'),
        _cutter3: this.get('_cutter3'),
      });
      fragments.add(fragment);
      return fragments;
    }

    // 4. Handle the edge fragments first.
    const firstCut = cuts[0];

    const lastCut = cuts[cuts.length - 1];
    if (this.circular()) {
      // Circular: handle wrapping fragment between lastCut and firstCut
      const s1 = lastCut.left;
      const t2 = firstCut.right;
      let bases = this.residueSlice(s1, t2);
      if (s1 !== t2 && cuts.length === 1) {
        // special case: non-blunt single cutter
        bases += this.residueSlice(t2, t2);
      }
      fragments.add({
        offset5: lastCut.offset,
        offset3: firstCut.offset,
        type: 'ds',
        circular: false,
        bases,
        _startIndex: startIndex + s1,
        _cutter5: digestableEnzyme,
        _cutter3: digestableEnzyme,
      });
    } else {
      // Linear: handle fragment before firstCut and after lastCut
      addFragment(
        0,
        firstCut.right,
        this.get('offset5'),
        firstCut.offset,
        this.get('_cutter5')!, // TODO: check if valid type coercion
        digestableEnzyme
      );
      addFragment(
        lastCut.left,
        this.length(),
        lastCut.offset,
        this.get('offset3'),
        digestableEnzyme,
        this.get('_cutter3')! // TODO: check if valid type coercion
      );
    }

    // 5. Handle middle fragments
    for (let i = 0; i < cuts.length - 1; i++) {
      const [prevCut, nextCut] = Array.from([cuts[i], cuts[i + 1]]);
      addFragment(
        prevCut.left,
        nextCut.right,
        prevCut.offset,
        nextCut.offset,
        digestableEnzyme,
        digestableEnzyme
      );
    }

    return fragments;
  }

  sanitizeFeatures(isCircular: boolean): void {
    if (!isCircular) {
      for (const collection of [this.annotations(), this.parts()]) {
        for (const feature of collection.models as Array<PolymerFeatureModel>) {
          if (sequtil.isWrap(feature.start(), feature.end())) {
            feature.trigger('destroy', feature, collection);
          }
        }
      }

      const crossOriginTranslations = this.getCrossOriginTranslations().flatMap(({regions}) => regions);
      for (const translation of crossOriginTranslations) {
        translation.trigger('destroy', translation, this.translations());
      }
      this.sequencePrimers().batchModify(() => {
        this.sequencePrimers().forEach((primer) => {
          primer.setBindPosition(this, primer.bindPosition(this), primer.overhangLength());
        });
      });

      for (const pair of this.primerPairs().models) {
        const [primerFwd, primerRev] = pair.getPrimers(this.sequencePrimers());
        if (primerFwd.end() > primerRev.end()) {
          pair.trigger('destroy', pair, this.primerPairs());
        }
      }
    }
  }

  /**
   * Get all translations that are cross origin by group.
   * If one translation region is cross origin, all regions that are part of the same group are also included,
   * and all regions in the result are sorted by position.
   */
  getCrossOriginTranslations(): Array<{name: string; regions: Array<Translation>}> {
    const translationGroupsById: {[id: string]: TranslationGroup} = groupByUniqueKey(
      this.translationGroups(),
      (g) => g.id
    );
    const translationsByGroupId: {[id: string]: Array<Translation>} = groupBy(
      this.translations().models,
      (translation) => translation.translationGroupId() ?? translation.cid
    );
    const crossOriginTranslations: Array<{name: string; regions: Array<Translation>}> = [];
    const seenCrossOriginGroupIds = new Set();
    for (const translationRegion of this.translations().models) {
      const groupId = translationRegion.translationGroupId();
      if (
        !seenCrossOriginGroupIds.has(groupId) &&
        sequtil.isWrap(translationRegion.start(), translationRegion.end())
      ) {
        if (groupId == null) {
          crossOriginTranslations.push({name: '', regions: [translationRegion]});
        } else {
          seenCrossOriginGroupIds.add(groupId);
          crossOriginTranslations.push({
            name: translationGroupsById[groupId].name,
            regions: sortedBy(translationsByGroupId[groupId], [(t) => t.start()]),
          });
        }
      }
    }
    return crossOriginTranslations;
  }

  _addModifiedNucleotides(newMods: Readonly<Array<ModifiedNucleotideAttributes>>): void {
    const sortedMods = [...this.modifiedNucleotides(), ...newMods].sort(
      (m1, m2) => m1.position - m2.position
    );
    this.set('modifiedNucleotides', sortedMods);
    // We need oligo bases layers to re-render immediately so that they pick up any mods that should display
    // as U/T-flexible bases
    this.triggerBaseChange();
  }

  opSetAttrs(params: {bases?: string; circular?: boolean; startIndex?: number}): void {
    // name, color, description, locked, folder_id do not affect the rendering of the plasmid.
    // We wil just let the server return them.

    let circular;
    if (params.hasOwnProperty('bases')) {
      const bases = util.sanitize(params.bases!, this.nucleotideType());
      this.set({
        bases,
        primerPairs: new PrimerPairs(),
        sequencePrimers: new SequencePrimers(),
        translations: new Translations(),
        annotations: new Annotations(),
      });
    }

    if (params.hasOwnProperty('helm')) {
      assert(false, 'Sequence HELM updates must be done server-side.');
    }

    // We set startIndex and circular at the same time to avoid the sequence getting into an invalid
    // state (i.e. circular and startIndex > 1).
    const toSet: {circular?: boolean; startIndex?: number} = {};
    if (params.hasOwnProperty('circular')) {
      ({circular} = params);
      if (this.get('circular') !== circular) {
        toSet.circular = circular;
      }
    }
    if (params.hasOwnProperty('startIndex')) {
      toSet.startIndex = params.startIndex;
    }
    // Sanitize features before updating the value of circular since listeners to circular may otherwise
    // operate on invalid features
    if (toSet.circular != null) {
      this.sanitizeFeatures(toSet.circular);
    }
    this.set(toSet);
  }

  opReindex(params: {current: number; desired: number}): void {
    let pos: number;
    const {current} = params;
    const {desired} = params;

    const length = this.length();
    if (current >= desired) {
      pos = current - desired;
    } else {
      pos = length - (desired - current);
    }

    this.suppressModify(function (this: Sequence): void {
      SequenceEdits.reindex(this, pos);
    });
  }

  opToggleCase({start, end, casing}: {start: number; end: number; casing: Casing}): void {
    this.changeCase(start, end, casing);
  }

  opExactReplaceBases({start, end, newBases}: {start: number; end: number; newBases: string}): void {
    this.exactReplaceBases(start, end, newBases);
  }

  opDelete(params: {start: number; end: number}): void {
    const {start} = params;
    const {end} = params;
    this.deleteBases(start, end);
  }

  opInsert(params: {
    pos: number;
    bases: string;
    modifiedNucleotides?: Readonly<Array<ModifiedNucleotideAttributes>>;
  }): void {
    // TODO: Deprecate this in favor of opInsertParts

    const {pos} = params;
    const bases = util.sanitize(params.bases, this.nucleotideType());

    this.insertBases(pos, bases);

    if (params.modifiedNucleotides) {
      const newMods = reindexModifiedNucleotides(params.modifiedNucleotides, pos);
      this._addModifiedNucleotides(newMods);
    }
  }

  opReplaceRegion(params: {
    start: number;
    end: number;
    bases: string;
    modifiedNucleotides?: Readonly<Array<ModifiedNucleotideAttributes>>;
  }): void {
    // TODO: Deprecate this in favor of opReplaceRegionWithParts
    const {start} = params;
    const {end} = params;
    const bases = util.sanitize(params.bases, this.nucleotideType());

    this.replaceBases(start, end, bases);

    if (params.modifiedNucleotides) {
      const newMods = reindexModifiedNucleotides(params.modifiedNucleotides, start);
      this._addModifiedNucleotides(newMods);
    }
  }

  opCreatePartAndReplace(params: {
    start: number;
    end: number;
    new_sequence: Sequence;
    tag_schema_field_id: number | null;
  }): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opInsertPart called without implementation (respondDiff should be false)');
  }

  opInsertPart(params: {
    pos: number;
    // API ID of the sequence to insert as a part.
    part: string;
    reverse: boolean;
    complement: boolean;
  }): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opInsertPart called without implementation (respondDiff should be false)');
  }

  opEditPart(params: {part: number; reverse: boolean; complement: boolean}): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opEditPart called without implementation (respondDiff should be false)');
  }

  opDeletePart(params: {part: number}): void {
    const part = this.parts().get(params.part);
    this.deleteBases(part.start(), part.end());
  }

  opSetSugarMod(params: {position: number; sugarMonomerId: string}): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opSetSugarMod called without implementation (respondDiff should be false)');
  }

  opSetBaseMod(params: {position: number; baseMonomerId: string}): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opSetBaseMod called without implementation (respondDiff should be false)');
  }

  opSetPhosphateMod(params: {position: number; phosphateMonomerId: string | null}): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opSetPhosphateMod called without implementation (respondDiff should be false)');
  }

  opBulkSetMods(params: BulkSetModsParams): void {
    // TODO: Implement for optimistic rendering (currently blocking operation)
    assert(false, 'opBulkSetMods called without implementation (respondDiff should be false)');
  }

  /**
   * Apply the specified operation to the sequence, modifying parts / features in the same way that the
   * server would modify the sequence (benchling/api/seqops.py:execute)
   *
   * @param {Object} attrs - An object of the form `{[opName]: opParams}`, matching server payload
   * @param {Object} [options] - Any extra metadata that a client operation may need that the server
   *                             wouldn't, e.g. the flattened bases when pasting parts for optimistic
   *                             rendering.
   */
  executeOperation(attrs: Partial<SequenceAttributes> | SeqOperation, options: SeqOpOptions): void {
    // Client-side changes.
    let fn;
    // Methods are bound when used
    /* eslint-disable @typescript-eslint/unbound-method */
    const operations: {
      [opName in keyof FrontendSeqOps]: (attr: FrontendSeqOps[opName], options: SeqOpOptions) => void;
    } = {
      toggle_case: this.opToggleCase,
      exact_replace_bases: this.opExactReplaceBases,
      reindex: this.opReindex,
      del: this.opDelete,
      insert: this.opInsert,
      replace_region: this.opReplaceRegion,
      create_part_and_replace: this.opCreatePartAndReplace,
      insert_part: this.opInsertPart,
      edit_part: this.opEditPart,
      delete_part: this.opDeletePart,
      set_sugar_mod: this.opSetSugarMod,
      set_base_mod: this.opSetBaseMod,
      set_phosphate_mod: this.opSetPhosphateMod,
      bulk_set_mods: this.opBulkSetMods,
    };
    const defaultOp = this.opSetAttrs;
    /* eslint-enable @typescript-eslint/unbound-method */
    // note that replace and reset are not handled (they do not set respondDiff: true in options)

    for (const [opName, op] of Object.entries(operations)) {
      if (attrs.hasOwnProperty(opName)) {
        fn = op.bind(this);
        // @ts-expect-error: this is correct but I can't convince TS
        return fn(attrs[opName], options);
      }
    }
    fn = defaultOp.bind(this);
    return fn(attrs, options);
  }

  /**
   * Perform a Backbone.save performing our custom operation based PATCH if options.respondDiff is specified,
   * returning an xhr that has a second promise, xhr.client, indicating when the client has re-rendered.
   *
   * xhr and xhr.client are the same for non-optimistic renders, but xhr.client will resolve almost
   * immediately (20ms) if it renders optimistically.
   *
   * Caller responsibilities:
   *  - handle showing an error message on the returned XHR
   *  - handle the case where xhr.client resolved while xhr rejected (rendered sequence will be in bad state)
   *
   * @param {Object} [options] - An object containing extra data (partial list below):
   *                             noteParts: An array containing objects of type APINotePart specifying note
   *                                        parts to create in the description of the new sequence.
   */
  // Normal Backbone save of key and value - TODO check if used
  save<K extends keyof SequenceAttributes>(
    key?: K,
    val?: SequenceAttributes[K],
    options?: SaveOptions
  ): SaveXHR<unknown>;
  // Normal Backbone save of partial attributes hash and options - TODO check if used
  save(key: Partial<SequenceAttributes>, val?: SaveOptions): SaveXHR<unknown>;
  // SeqOps custom save of SeqOperation and SeqOpOptions
  save(key: SeqOperation, val: SaveOptions): SaveXHR<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  save(key?: any, val?: any, options?: any): SaveXHR<unknown> {
    // Handle dictionary vs single key-val pair.
    let attrs: (Partial<SequenceAttributes> | SeqOperation) & {respondDiff?: boolean};
    let xhr: SaveXHR<unknown>;
    let opts: SaveOptions;
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (key === null || typeof key === 'object') {
      attrs = key;
      opts = val;
    } else {
      // @ts-expect-error TODO fix weird argument types
      (attrs = {})[key] = val;
      opts = {};
    }

    opts = opts || {};

    if (!this.isNew() && opts.respondDiff) {
      attrs.respondDiff = true;
      // Let parse() know that we are parsing a custom save request.
      opts.save = true;
      // Do not set the values directly on; executeOperation will handle this.
      opts.wait = true; // TODO: This is still incorrect, @set(attrs) is happening after successful save.
      // As mentioned below, since we are waiting 20ms before doing the update, it is technically possible
      // that the server responds before the client optimistically renders (via resolution of
      // shouldRenderClientDeferred). We thus thread through the call of executeOperation to the `parse`
      // method so we can ensure that the optimistic render happens before we apply the server changes.
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      const executeClientRenderOnce = _.once(() => this.executeOperation(attrs, opts));
      opts.renderClient = executeClientRenderOnce;

      // Now do the normal save
      xhr = super.save(attrs, opts);
      const clientRenderedDeferred = $.Deferred();
      if (!xhr) {
        // The result of save is false when validation fails. Just return an immediately-rejected promise.
        return getRejectedSaveXHRForValidationFailure<unknown>(this);
      }
      xhr.client = clientRenderedDeferred.promise();

      // For optimistic rendering, the XHR may:
      //  - fail immediately (because of an oustanding XHR, see backbone-extensions.js:Backbone.sync)
      //  - succeed immediately (testing)
      //  - fail almost immediately (empirically within 10s of milliseconds) if the user is offline.
      //    This case is especially important to handle, otherwise, if the user briefly loses internet access,
      //    we will optimistically render, the save will fail, and we will try to revert by re-fetching,
      //    which will also fail, and the user will be stuck with a loading screen and will have to refresh.
      // Thus, we either:
      //  a. optimistically render after the XHR succeeds or 20ms passes - whichever happens first.
      //  b. avoid the optimistic render if the XHR fails within 20ms.
      //
      // NB: The amount of time it takes to render the change is on the order of low 100s of milliseconds,
      //     so 20ms extra delay isn't noticeable.
      const shouldRenderClientDeferred = $.Deferred();
      // TODO: see if these need to be bound
      // eslint-disable-next-line @typescript-eslint/unbound-method
      xhr.then(shouldRenderClientDeferred.resolve, shouldRenderClientDeferred.reject);
      setTimeout(() => {
        if (xhr.state() === 'rejected') {
          shouldRenderClientDeferred.reject();
        } else {
          shouldRenderClientDeferred.resolve();
        }
      }, 20);

      // Optimistically render if the XHR hasn't failed within 20ms....
      shouldRenderClientDeferred.done(() => {
        executeClientRenderOnce();
      });

      // Inform the client after optimistic rendering has happened / was avoided.
      // TODO: see if these need to be bound
      // eslint-disable-next-line @typescript-eslint/unbound-method
      shouldRenderClientDeferred.then(clientRenderedDeferred.resolve, clientRenderedDeferred.reject);
    } else {
      if (opts.noteParts) {
        // eslint-disable-next-line no-restricted-syntax
        xhr = super.save({...attrs, noteParts: opts.noteParts}, _.omit(opts, 'noteParts'));
        // Sending additional data through a Backbone save writes the data as a model attribute; unset
        // attribute after save to avoid changing Backbone model.
        this.unset('noteParts');
      } else {
        // eslint-disable-next-line prefer-rest-params
        xhr = super.save(...arguments);
      }
      if (!xhr) {
        // The result of save is false when validation fails. Just return an immediately-rejected promise.
        return getRejectedSaveXHRForValidationFailure<unknown>(this);
      }
      // We have to wait for the server response, so we are done rendering whenever the xhr is done.
      xhr.client = xhr;
    }
    return xhr;
  }

  toJSON(options?: {omitSubpartFeatures: boolean}): ObjectHash {
    if (options == null) {
      // @ts-expect-error Will be populated with _.defaults below
      options = {};
    }
    _.defaults(options, {omitSubpartFeatures: true});
    const json = super.toJSON(options);

    if (options!.omitSubpartFeatures) {
      json.annotations = json.annotations.reject((a: Annotation) => a.fromSubPart());
      json.translations = json.translations.reject((t: Translation) => t.fromSubPart());
      json.sequencePrimers = json.sequencePrimers.reject((sp: SequencePrimer) => sp.fromSubPart());
      // Assemblies abuse backbone attributes and sometimes primerPairs is not actually a PrimerPairCollection
      if (json.primerPairs instanceof PrimerPairCollection) {
        json.primerPairs = json.primerPairs.reject((pp: PrimerPair) => pp.fromSubPart());
      }
    }

    // Server expects underscores, not camelcase.
    json.sequence_primers = json.sequencePrimers;
    json.primer_pairs = json.primerPairs;
    json.fragment_results = json.fragmentResults;
    json.modified_nucleotides = json.modifiedNucleotides;

    delete json.sequencePrimers;
    delete json.primerPairs;
    delete json.fragmentResults;
    delete json.modifiedNucleotides;

    return json;
  }

  parse(
    response: SequenceServerResponse,
    options?: {save: boolean; renderClient: () => {}}
  ): Partial<SequenceAttributes> {
    if (options != null ? options.save : undefined) {
      // Update the client before we do anything that the server is modifying. options.renderClient should be
      // idempotent--it may have already been called for optimistic rendering (which renders with a short
      // delay to account for some connectivity edge cases), so it may have not been fired yet. The
      // server-returned sequence data assumes that the client has already rendered (e.g. for part id updates)
      if (
        assertAndContinue(
          typeof options!.renderClient === 'function',
          'Expected renderClient function to exist.'
        )
      ) {
        options!.renderClient();
      }
    }
    // TODO: make new response object instead of mutating argument

    // Unfortunately, this replaces any existing collections with new collection objects.
    // That means that if you listen for @listenTo(sequence.annotations(), 'change', ->),
    // you won't get triggered. Instead, you have to both listen to the annotations collection,
    // and @listenTo(sequence, 'change:annotations', -> reregister listener).

    // We *could* change it so we reset the collection instead:
    //   annotations = @annotations() or new Annotation.Collection()
    //   annotations.reset(resp.annotations, {parse: true})
    //   resp.annotations = annotations
    //
    // However, this has other problems. Change events will get fired in the middle of parse,
    // while the model is in a bad state, instead of after parsing/merging like usual. This
    // can cause lots of problems (annotation collection event fires, but bases haven't been
    // updated yet, etc.). So for now... we're sticking with replacing the collection object.

    if (response.annotations != null) {
      // @ts-expect-error mutating object to match desired type
      response.annotations = new Annotations(response.annotations, {
        parse: true,
        sequenceID: response.id,
      });
    }
    if (response.translations != null) {
      // @ts-expect-error mutating object to match desired type
      response.translations = new Translations(response.translations, {
        parse: true,
        sequenceID: response.id,
      });
    }

    if (response.sequence_primers != null) {
      // @ts-expect-error snake_case -> camelCase not captured in types yet
      response.sequencePrimers = new SequencePrimers(response.sequence_primers, {
        parse: true,
        sequenceID: response.id,
      });
      // @ts-expect-error: Field is still expected in server response type
      delete response.sequence_primers;
      // @ts-expect-error snake_case -> camelCase not captured in types yet
    } else if (response.sequencePrimers != null) {
      // FIXME: alignments fucked up
      // @ts-expect-error snake_case -> camelCase not captured in types yet
      delete response.sequencePrimers; // let it be set by defaults. Remove after migration.
    }

    if (response.primer_pairs != null) {
      // @ts-expect-error snake_case -> camelCase not captured in types yet
      response.primerPairs = new PrimerPairs(response.primer_pairs, {
        parse: true,
        sequenceID: response.id,
      });
      // @ts-expect-error
      delete response.primer_pairs;
      // @ts-expect-error snake_case -> camelCase not captured in types yet
    } else if (response.primerPairs != null) {
      // FIXME: alignments fucked up.
      // @ts-expect-error snake_case -> camelCase not captured in types yet
      delete response.primerPairs; // let it be set by defaults. Remove after migration.
    }

    if (response.parts != null) {
      // @ts-expect-error Will be fixed with import codemod
      response.parts = new PartModels.Collection(
        response.parts.filter((p) => p.sequence != null),
        {parse: true}
      );
    }

    if (response.fragment_results != null) {
      // @ts-expect-error snake_case -> camelCase not captured in types yet
      response.fragmentResults = new FragmentResult.Collection(response.fragment_results, {parse: true});
      // @ts-expect-error: Field is still expected in server response type
      delete response.fragment_results;
    }

    if (response.assembly != null) {
      response.assembly = new AssemblyModels.Assembly(response.assembly, {parse: true});
    }

    if (response.modified_nucleotides) {
      // @ts-expect-error snake_case -> camelCase not captured in types yet
      response.modifiedNucleotides = response.modified_nucleotides;
      // @ts-expect-error: Field is still expected in server response type
      delete response.modified_nucleotides;
    }

    // @ts-expect-error Fix when we're not mutating the response object anymore
    return response;
  }

  _updatePartFeatures(): void {
    const parts = this.parts();
    const seqRef = this;

    this.annotations().batchModify(function (this: Annotations): Annotation {
      this.remove(this.where({fromSubPart: true}));
      return this.add(parts.annotations(seqRef));
    });

    this.translations().batchModify(function (this: Translations): Translation {
      this.remove(this.where({fromSubPart: true}));
      return this.add(parts.translations(seqRef));
    });

    const nonHoistedTranslationGroups = this.translationGroups().filter(({fromSubPart}) => !fromSubPart);
    this.set('translationGroups', [...nonHoistedTranslationGroups, ...parts.translationGroups(seqRef)]);

    this.sequencePrimers().batchModify(function (this: SequencePrimers): SequencePrimer {
      this.remove(this.where({fromSubPart: true}));
      return this.add(parts.sequencePrimers(seqRef));
    });

    this.primerPairs().batchModify(function (this: PrimerPairs): PrimerPair {
      this.remove(this.where({fromSubPart: true}));
      return this.add(parts.primerPairs(seqRef));
    });
    this._cleanPairs();
  }

  concat(o: Sequence): Sequence {
    /*
      concats two sequences together
        - does not care about biological relevance/overhangs.
        - major properties inherited from parent
        - used as utility function
    */
    const newAnnotations = new Annotations();

    this.annotations().each(function (this: Sequence, ann: Annotation): Annotation {
      const newann = ann.clone();
      if (newann.get('end') === 0) {
        newann.set({end: this.length()});
      }
      newann.unset('id');
      return newAnnotations.add(newann);
    }, this);

    o.get('annotations').each(function (this: Sequence, ann: Annotation): Annotation {
      const newann = ann.clone(this.length());
      if (newann.get('end') === this.length()) {
        newann.set({end: 0});
      }
      newann.unset('id');
      return newAnnotations.add(newann);
    }, this);

    const sequence = new Sequence({annotations: newAnnotations, bases: this.bases() + o.bases()});
    return sequence.set({
      offset5: !this.isEmpty() ? this.get('offset5') : o.get('offset5'),
      offset3: !o.isEmpty() ? o.get('offset3') : this.get('offset3'),
    });
  }

  triggerBaseChange(): Sequence {
    // TODO remove return?
    return this.trigger('change:bases', this, this.bases(), {partChange: true});
  }

  changeCase(start: number, end: number, casing: Casing): void {
    assert(start != null && end != null, `Start (${start}) and end (${end}) must be defined for changeCase`);
    if (end <= start) {
      if (end !== 0) {
        // Avoid infinite looping by changeCase(0, 0) over and over.
        this.changeCase(0, end, casing);
      }
      this.changeCase(start, this.length(), casing);
      return;
    }

    const caseFn = (() => {
      switch (casing) {
        case 'upper':
          return (s: string) => s.toUpperCase();
        case 'lower':
          return (s: string) => s.toLowerCase();
        default:
          throw new Error('Illegal casing argument.');
      }
    })();

    this.set(
      'bases',
      this.bases().slice(0, start) + caseFn(this.bases().slice(start, end)) + this.bases().slice(end)
    );
  }

  exactReplaceBases(start: number, end: number, newBases: string): void {
    assert(
      start != null && end != null,
      `Start (${start}) and end (${end}) must be defined for exactReplaceBases`
    );
    if (end <= start) {
      if (end !== 0) {
        // Avoid infinite looping by switchBase(0, 0) over and over.
        this.exactReplaceBases(0, end, newBases.slice(this.length() - start, newBases.length));
      }
      this.exactReplaceBases(start, this.length(), newBases.slice(0, this.length() - start));
      return;
    }

    assert(
      this.parts().getSequencePartsBetween(start, end).length === 0,
      'Invalid region to exact replace bases'
    );
    this.set('bases', this.bases().slice(0, start) + newBases + this.bases().slice(end));

    this.set(
      'modifiedNucleotides',
      this.modifiedNucleotides().filter((mod) => mod.position < start || mod.position >= end)
    );
  }

  insertBases(pos: number, newBases: string): void {
    this.suppressModify(function (this: Sequence): void {
      SequenceEdits.insertBases(this, pos, newBases);
    });
  }

  deleteBases(start: number, end: number): void {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return this.suppressModify(function (this: Sequence): void {
      SequenceEdits.deleteBases(this, start, end);
    });
  }

  deletePart(part: Part): void {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return this.suppressModify(function (this: Sequence): void {
      SequenceEdits.deleteBases(this, part.start(), part.end());
    });
  }

  /**
   * Re-index feature models to match the selection.
   *
   * @param features   Sequence features to reindex
   * @param start      Start position of the selection in the context of the original sequence
   */
  private reindexFeatures<FeatureAttributes extends PolymerFeatureAttributes>(
    features: Array<FeatureAttributes>,
    start: number
  ): Array<FeatureAttributes> {
    const seqLength = this.length();
    return features.map((feature: FeatureAttributes) => ({
      ...feature,
      start: sequtil.getReindexedPosition(feature.start, start, seqLength),
      end: sequtil.getReindexedPosition(feature.end, start, seqLength),
    }));
  }

  /**
   * Filter out features not contained at all by the selection,
   * given that the features have already been reindexed to match the selection.
   *
   * @param features   Sequence features to filter
   * @param coercedEnd End position of the selection in the context of the sequence to be created, after reindexing
   */
  private filterOutNotContainedFeatures<FeatureAttributes extends PolymerFeatureAttributes>(
    features: Array<FeatureAttributes>,
    coercedEnd: number
  ): Array<FeatureAttributes> {
    return features.filter(
      (feature) => (feature.start >= feature.end && feature.end > 0) || feature.start < coercedEnd
    );
  }

  /**
   * When the option to use primer bases is selected, gets the bases for the selection
   * with the relevant primer bases including overhang.
   *
   * @param startPrimer Selected primer at start of selection, if one exists
   * @param endPrimer   Selected primer at end of selection, if one exists
   * @param start       Start position of the selection in the context of the original sequence
   * @param end         End position of the selection in the context of the original sequence
   */
  private getBasesWithPrimerBases(
    startPrimer: BasePrimer | null | undefined,
    endPrimer: BasePrimer | null | undefined,
    start: number,
    end: number
  ): string {
    const seqLength = this.length();
    const primersOverlap =
      startPrimer != null &&
      endPrimer != null &&
      sequtil.isRangeOverlap(startPrimer.start(), startPrimer.end(), endPrimer.start(), endPrimer.end());
    if (primersOverlap) {
      // Primers overlap, so new sequence consists entirely of primer bases.
      // Arbitrarily use start primer's bases in region where it overlaps with end primer.
      const overlapLength = sequtil.length(endPrimer.start(), startPrimer.end(), seqLength);
      const endBases = util
        .revcomp(util.sanitize(endPrimer.bases(), this.nucleotideType()), this.baseTable())
        .substring(overlapLength);
      return startPrimer.bases() + endBases;
    } else {
      let baseEnd;
      let baseStart;
      let leftPrimerInsert;
      let rightPrimerInsert;
      if (startPrimer != null) {
        // Replace sequence bases with primer bases.
        leftPrimerInsert = util.sanitize(startPrimer.bases(), this.nucleotideType());
        baseStart = startPrimer.end();
      } else {
        leftPrimerInsert = '';
        baseStart = start;
      }

      if (endPrimer != null) {
        rightPrimerInsert = util.revcomp(
          util.sanitize(endPrimer.bases(), this.nucleotideType()),
          this.baseTable()
        );
        baseEnd = endPrimer.start();
      } else {
        rightPrimerInsert = '';
        baseEnd = end;
      }

      // Equal baseStart and baseEnd means either all bases or no bases, and
      // the latter case only happens when baseStart/End is contracted due to
      // a primer using its own bases. So if we have a start or endPrimer we
      // know we want no bases.
      // Otherwise, pre-primer baseStart/baseEnd would have been for an
      // overlapping selection, which is impossible.
      if (
        sequtil.mod(baseStart, seqLength) === sequtil.mod(baseEnd, seqLength) &&
        (startPrimer != null || endPrimer != null)
      ) {
        return leftPrimerInsert + rightPrimerInsert;
      }
      return (
        leftPrimerInsert + sequtil.residueSlice(this.bases(), baseStart, baseEnd, true) + rightPrimerInsert
      );
    }
  }

  /**
   * Adjust feature positions to account for additional bases at the start of the selection.
   *
   * @param features          Sequence feature attributes to adjust
   * @param padLength         Number of bases to pad on the left side (start of selection) of each feature
   * @param selectionLength   Length of sequence selection that the new sequence will be created from
   */
  private padFeaturesAtStart(
    features: Array<{start: number; end: number; bind_position?: number}>,
    padLength: number,
    selectionLength: number
  ): void {
    for (const feature of features) {
      feature.start += padLength;
      if (feature.end !== 0) {
        feature.end += padLength;
      } else {
        feature.end = selectionLength + padLength;
      }
      if (feature.bind_position != null) {
        feature.bind_position += padLength;
      }
    }
  }

  private fixFeatureEnds(features: Array<PolymerFeatureAttributes>, newLength: number): void {
    for (const feature of features) {
      if (feature.end === 0) {
        feature.end = newLength;
      }
    }
  }

  private cleanFeatures<T extends PolymerFeatureAttributes>(features: Array<T>, newLength: number): Array<T> {
    return features.map((feature) => ({
      ...feature,
      end: sequtil.mod(feature.end, newLength),
      fromSubPart: false,
    }));
  }

  /**
   * If includePartiallyContained is true, filter to any annotations that overlap with any part
   * of the selection, and truncate start and end positions of any partially contained annotations to fall
   * within the selection. Otherwise, only include annotations that are fully contained by the selection.
   *
   * Assumes that the given annotations have already been reindexed to match the selection.
   * coercedEnd is the end position of the selection in the context of the sequence to be created,
   * after reindexing where the position at the end of the sequence is 0.
   * The start position in the context of the sequence to be created is always 0.
   */
  private filterAndClampAnnotations(
    includePartiallyContained: boolean | undefined,
    annotations: Array<AnnotationAttributesWithOptionalID>,
    coercedEnd: number
  ): Array<AnnotationAttributesWithOptionalID> {
    const seqLength = this.length();
    let filteredAnnotations: Array<AnnotationAttributesWithOptionalID> = [];

    if (includePartiallyContained) {
      const clampedEnd = coercedEnd === 0 ? seqLength : coercedEnd;
      filteredAnnotations = this.filterOutNotContainedFeatures(annotations, clampedEnd);
      // Clamp annotations.
      filteredAnnotations = filteredAnnotations.flatMap((annotation) => {
        if (annotation.start < annotation.end) {
          if (annotation.end >= clampedEnd) {
            annotation.end = clampedEnd;
          }
        } else {
          if (annotation.start >= clampedEnd) {
            annotation.start = 0;
          }
          if (annotation.end > clampedEnd) {
            annotation.end = clampedEnd;
          }
        }
        if (annotation.end <= annotation.start && annotation.end > 0) {
          // Need to split annotation, omit ID so backbone doesn't de-duplicate later.
          const {id, ...dupedAnnotation} = annotation;
          const ann1 = {...dupedAnnotation, start: 0};
          const ann2 = {...dupedAnnotation, end: 0};
          return [ann1, ann2];
        }
        return annotation;
      });

      for (const annotation of filteredAnnotations) {
        assert(
          0 <= annotation.start &&
            annotation.start < seqLength &&
            0 <= annotation.end &&
            annotation.end < seqLength,
          `Invalid annotation indices: ${annotation.start}, ${annotation.end}, ${seqLength}`
        );
      }
    } else {
      filteredAnnotations = annotations.filter((a) => sequtil.isRangeWithin(a.start, a.end, 0, coercedEnd));
    }
    return filteredAnnotations;
  }

  /**
   * Filters out multi-region translations that would be split by the given selection.
   * When includePartiallyContained is false, also filter out translations where not all regions
   * are fully contained by the selection.
   */
  private filterWholeTranslationGroups(
    translationRegionGroups: Array<TranslationRegionGroupForCreate>,
    start: number,
    end: number,
    includePartiallyContained: boolean | undefined
  ): Array<TranslationRegionGroupForCreate> {
    const seqLength = this.length();
    // All translations must have at least one codon, so if the selection is too small,
    // the sequence cannot have any translations on it.
    if (sequtil.length(start, end, seqLength) < CODON_LENGTH) {
      return [];
    }
    const filteredTranslations: Array<TranslationRegionGroupForCreate> = [];
    const coercedEnd = end === seqLength ? 0 : end;
    for (const regionGroup of translationRegionGroups) {
      const {regions} = regionGroup;
      const groupStart = regions[0].start;
      const groupEnd = regions[regions.length - 1].end;

      if (includePartiallyContained) {
        /**
         * Reindex translation and selection positions to the start of the translation
         * so that we don't have to deal with cross-origin translation regions.
         *
         * Example of a selection that splits a translation group:
         * Before reindexing:
         * |--------|                         selection
         *       |----|                       translation region 1
         * ---|           |------             translation region 2
         *
         * After reindexing to the translation start:
         * ---|            |---               cross-origin selection
         * |----|                             translation region 1
         *          |---------|               translation region 2
         *
         * The selection starts in the second region but ends in the first, splitting the original
         * region ordering so this translation will not be kept.
         */
        const reindexedGroupStart = 0;
        const reindexedGroupEnd = sequtil.getReindexedPosition(groupEnd, groupStart, seqLength);
        const reindexedStart = sequtil.getReindexedPosition(start, groupStart, seqLength);
        const reindexedEnd = sequtil.getReindexedPosition(coercedEnd, groupStart, seqLength);
        const selectionSplitsTranslation =
          sequtil.isWrap(reindexedStart, reindexedEnd) &&
          sequtil.baseInRange(reindexedStart, reindexedGroupStart, reindexedGroupEnd);
        if (!selectionSplitsTranslation) {
          filteredTranslations.push(regionGroup);
        }
      } else if (sequtil.isRangeWithin(groupStart, groupEnd, start, coercedEnd)) {
        // The range of the translation group goes from the start of the leftmost translation to the end of
        // the rightmost translation (we don't allow groups to wrap the origin in circular sequences).
        // Valid translation groups are those fully encompassed by the selection range.
        filteredTranslations.push(regionGroup);
      }
    }
    return filteredTranslations;
  }

  /**
   * Clamps the start and end positions of the given translation region such that each translation
   * starts with a full codon that matches the existing translation or continues a codon from the previous region.
   *
   * @param translation Attributes of the translation region, reindexed to match the selection
   * @param clampedEnd Reindexed position of the end of the selection
   * @param upstreamBases Number of trailing partial codon bases from the previous region to account for
   * @param trimContinuedCodonBases Whether to exclude bases on this region that continue translating a codon
   * from the previous region
   */
  private clampTranslation(
    translation: TranslationAttributes,
    clampedEnd: number,
    upstreamBases: number,
    trimContinuedCodonBases: boolean
  ): TranslationAttributes {
    const seqLength = this.length();
    const upstreamOffset = (CODON_LENGTH - upstreamBases) % CODON_LENGTH;
    let newStart = translation.start;
    let newEnd = translation.end;

    if (translation.strand === 1) {
      if (translation.start < translation.end) {
        // If the previous region in the translation had trailing partial codon bases that were not included,
        // clamp the start of this region to the first full codon.
        // Otherwise, this region finishes a previous codon so do not clamp the start.
        newStart += trimContinuedCodonBases ? upstreamOffset : 0;
      } else if (translation.start >= clampedEnd) {
        // A reindexed cross-origin region means that this is the first contained region of the translation
        // so we always clamp to the first full codon of the original reading frame.
        const upstreamLengthOutsideSelection = seqLength - translation.start + upstreamBases;
        newStart = (CODON_LENGTH - (upstreamLengthOutsideSelection % CODON_LENGTH)) % CODON_LENGTH;
        // If this region does not have a full codon within the selection, clamp to 0
        if (newStart >= translation.end) {
          newStart = 0;
        }
      }
      newEnd = Math.min(translation.end, clampedEnd);
    } else {
      if (translation.end >= clampedEnd) {
        // This is the first contained region of the translation.
        // On a reversed translation, reading frames are also reversed so clamp the end to the "first"
        // full codon, keeping the original reading frame.
        const upstreamLengthOutsideSelection = translation.end - clampedEnd + upstreamBases;
        // fullCodonEnd is always >= 0 because we've already checked that the selection is at least 3 bases
        const fullCodonEnd =
          clampedEnd - ((CODON_LENGTH - (upstreamLengthOutsideSelection % CODON_LENGTH)) % CODON_LENGTH);
        newEnd = Math.min(translation.end, fullCodonEnd);
      } else {
        // If the previous region in the translation had trailing partial codon bases that were not included,
        // clamp the end of this region to the first full codon.
        // Otherwise, this region finishes a previous codon so do not clamp the end.
        newEnd -= trimContinuedCodonBases ? upstreamOffset : 0;
      }
      // If the region starts before the selection, also clamp the start
      if (translation.start >= translation.end && translation.start >= clampedEnd) {
        newStart = 0;
      }
    }

    // clampedEnd is the end of the sequence to be created, so convert to the actual sequence end position of 0
    if (newEnd >= clampedEnd) {
      newEnd = 0;
    }

    return {...translation, start: newStart, end: newEnd};
  }

  /**
   * Convert a list of translation regions into a list of multi-region translations by group ID.
   * Regions in each group are sorted by start position.
   */
  private getTranslationRegionGroups(
    translations: Array<TranslationAttributes>
  ): Array<TranslationRegionGroupForCreate> {
    const noGroupId = 'none';
    const sortedTranslations = sortedBy(translations, ['start']);

    const translationsByGroupId: {[id: string]: Array<TranslationAttributes>} = groupBy(
      sortedTranslations,
      (t) => t.translationGroupId ?? noGroupId
    );
    let regionGroups: Array<TranslationRegionGroupForCreate> = [];
    for (const [groupId, translations] of Object.entries(translationsByGroupId)) {
      if (groupId === noGroupId) {
        const loneGroups: Array<TranslationRegionGroupForCreate> = translations.map((t) => ({
          groupId: null,
          regions: [t],
        }));
        regionGroups = regionGroups.concat(loneGroups);
      } else {
        regionGroups.push({groupId: Number.parseInt(groupId, 10), regions: translations});
      }
    }
    return regionGroups;
  }

  /**
   * Filters and clamps a multi-region translation to the portions of the regions that are contained
   * by the selection, which is defined by 0 to clampedEnd. The translated amino acids of the clamped
   * regions will match the original translation.
   *
   * @param regions     The regions of a multi-region translation, in order of the original translation
   *                    and reindexed to match the selection.
   * @param clampedEnd  The reindexed end position of the selection in the context of the sequence to be created
   */
  private filterAndClampPartiallyContainedTranslations(
    regions: Array<TranslationAttributes>,
    clampedEnd: number
  ): Array<TranslationAttributes> {
    let upstreamBases = 0;
    const seqLength = this.length();
    const maybeRegionsToInclude: Array<TranslationAttributes> = [];
    const orderedRegions = regions[0].strand === 1 ? regions : regions.reverse();
    for (const translation of orderedRegions) {
      // Is fully or partially contained by the selection
      const isContained =
        (translation.start >= translation.end && translation.end > 0) || translation.start < clampedEnd;
      // Number of trailing bases that may translate to a full codon when combined with bases in the next region
      const nextUpstreamBases =
        (sequtil.length(translation.start, translation.end, seqLength) + upstreamBases) % CODON_LENGTH;
      const isFirstRegion = maybeRegionsToInclude.length === 0;

      if (isContained) {
        const clampedRegion = this.clampTranslation(translation, clampedEnd, upstreamBases, isFirstRegion);
        const clampedLength = sequtil.length(clampedRegion.start, clampedRegion.end, seqLength);

        // If this is the first contained region, add it if it's long enough to translate to a full codon,
        // accounting for any partial continued bases in the next region.
        if (
          !isFirstRegion ||
          (clampedLength >= nextUpstreamBases && nextUpstreamBases > 0) ||
          (nextUpstreamBases === 0 && clampedLength >= CODON_LENGTH)
        ) {
          maybeRegionsToInclude.push(clampedRegion);
        }
      } else if (!isFirstRegion) {
        // We have added all regions that are partially or fully contained by the selection
        break;
      }
      upstreamBases = nextUpstreamBases;
    }
    // Don't add the translation if it's shorter than one codon across all regions
    const totalLength = sum(maybeRegionsToInclude.map((t) => sequtil.length(t.start, t.end, clampedEnd)));
    if (totalLength >= CODON_LENGTH) {
      return maybeRegionsToInclude;
    }
    return [];
  }

  /**
   * Filters, clamps and reindexes translations to match the selection.
   *
   * If includePartiallyContained is true, filter to any translations that overlap with any part
   * of the selection. The start and end positions of partially contained translations are clamped so that
   * they fall fully within the selection, such that the translated codons are the same as the
   * translation on the original sequence.
   * Otherwise, only include translations that are fully contained by the selection.
   */
  private handleTranslationsFromIndices(
    includePartiallyContained: boolean | undefined,
    translations: Array<TranslationAttributes>,
    start: number,
    end: number
  ): Array<TranslationAttributes> {
    const seqLength = this.length();
    let filteredTranslations: Array<TranslationAttributes> = [];
    const translationRegionGroups = this.getTranslationRegionGroups(translations);

    // Filtering multi-region translations relies on the original positions and ordering of regions
    // so we filter before reindexing to match the selection.
    const filteredRegionGroups = this.filterWholeTranslationGroups(
      translationRegionGroups,
      start,
      end,
      includePartiallyContained
    );
    const reindexedEnd = sequtil.getReindexedPosition(end, start, seqLength);
    const reindexedGroups = filteredRegionGroups.map(({groupId, regions}) => ({
      groupId,
      regions: this.reindexFeatures(regions, start),
    }));

    if (includePartiallyContained) {
      const clampedEnd = reindexedEnd === 0 ? seqLength : reindexedEnd;
      for (const {regions} of reindexedGroups) {
        filteredTranslations = filteredTranslations.concat(
          this.filterAndClampPartiallyContainedTranslations(regions, clampedEnd)
        );
      }
      filteredTranslations = filteredTranslations.filter((t) => !sequtil.isWrap(t.start, t.end));

      for (const _translation of filteredTranslations) {
        assert(
          0 <= _translation.start &&
            _translation.start < seqLength &&
            0 <= _translation.end &&
            _translation.end < seqLength,
          `Invalid translation indices: ${_translation.start}, ${_translation.end}, ${seqLength}`
        );
      }
    } else {
      // Translations that aren't fully contained were filtered out in filterWholeTranslationGroups
      filteredTranslations = reindexedGroups.flatMap(({regions}) => regions);
    }
    return filteredTranslations;
  }

  private getNewNucleotideType(
    useNewSequenceType: boolean,
    sequenceType: CreateFromIndicesOptions['type'],
    primers: CreateFromIndicesOptions['primers']
  ): NucleotideType {
    const singleCopiedPrimer = getSingleCopiedPrimer(primers || [null, null]);

    if (singleCopiedPrimer) {
      return singleCopiedPrimer.nucleotideType();
    } else if (useNewSequenceType) {
      if (sequenceType === SequenceType.DOUBLE_STRANDED) {
        return NucleotideType.DNA;
      } else if (sequenceType === SequenceType.SINGLE_STRANDED) {
        return NucleotideType.RNA;
      } else {
        assertAndContinue(false, 'Cannot create new oligo from sequence selection');
        return this.nucleotideType();
      }
    } else {
      return this.nucleotideType();
    }
  }

  /**
   * Filters the modified nucleotides on the sequence to those that fall within the given selection
   * and reindexes them to match the selection.
   *
   * @param start   Start position of the selection in the context of the original sequence
   * @param end     End position of the selection in the context of the original sequence
   */
  private filterAndReindexMods(start: number, end: number): Array<ModifiedNucleotideAttributes> {
    const seqLength = this.length();
    return this.modifiedNucleotides()
      .filter((mod) => mod.position >= start && mod.position < end)
      .map((mod) => ({
        ...mod,
        position: sequtil.getReindexedPosition(mod.position, start, seqLength),
      }));
  }

  /**
   * @param {Object} options - An object containing data about the sequence to be created (partial list):
   *                           bases: A string of bases to replace the existing bases with.
   *                           name: A string of the new sequence's name.
   */
  createFromIndices(start: number, end: number, options?: CreateFromIndicesOptions): Sequence {
    let newBases: string;
    if (options == null) {
      options = {};
    }

    const seqLength = this.length();
    const partAttributes = this.parts()
      .getSequencePartsContainedWithin(seqLength, start, end)
      .map((p) => util.clonePartAttributes(p.attributes, start, end, seqLength));
    const parentVersionId = options.parentVersionId;

    const [startPrimer, endPrimer] = options.primers || [];
    // Only linearize for primers if they are fully contained within selection
    const linearizeForPrimers =
      options.primerBases &&
      ((startPrimer && sequtil.isRangeWithin(startPrimer.start(), startPrimer.end(), start, end)) ||
        (endPrimer && sequtil.isRangeWithin(endPrimer.start(), endPrimer.end(), start, end)));
    const fullCircular = sequtil.fullCircular(this.circular(), start, end, seqLength);
    // Only keep circular if we have a full selection and are not creating RNA
    const keepFullCircular =
      fullCircular && !linearizeForPrimers && options.type !== SequenceType.SINGLE_STRANDED;

    // When a subpart is contained in the selection, any subpart features would be duplicated
    // into the new sequence. This filters out those duplicates
    const shouldRejectSubPartFeature = (feature: PolymerFeatureModel): boolean => {
      if (!feature.fromSubPart()) {
        return false;
      }
      const parts = this.parts().getSequencePartsAt(feature.start());
      // If the selection would entirely contain the sub-part feature but only partially contain the part,
      // then we "promote" this feature from a sub-part feature to a regular feature so that it's kept. In the
      // future, it may make sense to drop this feature so that sub-part features are never promoted to the
      // outer sequence.
      return parts.some((part) => isCircularRangeWithin(part.start(), part.end(), start, end, seqLength));
    };

    const featureIsOnReverseStrandOfOutput = (feature: PolymerFeatureModel): boolean => {
      if (options?.reverseComplement) {
        return feature.strand() === 1;
      } else {
        return feature.strand() === -1;
      }
    };

    // Only include modified nucleotides when features are preserved and we are taking the forward copy
    // since we cannot take the revcomp of mods.
    const modifiedNucleotides: Array<ModifiedNucleotideAttributes> =
      options.annotations && !options.reverseComplement ? this.filterAndReindexMods(start, end) : [];

    let annotations: Array<AnnotationAttributesWithOptionalID> = [];
    let translations: Array<TranslationAttributes> = [];

    if (options.annotations) {
      let annotationModels: Array<Annotation> = this.annotations().reject(shouldRejectSubPartFeature);
      let translationModels: Array<Translation> = this.translations().reject(shouldRejectSubPartFeature);

      // When an RNA sequence is being created from one strand of a DNA sequence,
      // discard features on the other strand, except for primers which are special-cased.
      if (options.type === SequenceType.SINGLE_STRANDED) {
        annotationModels = this.annotations().reject(featureIsOnReverseStrandOfOutput);
        translationModels = this.translations().reject(featureIsOnReverseStrandOfOutput);
      }

      annotations = this.reindexFeatures(
        annotationModels.map((a) => a.toJSON()),
        start
      );
      translations = translationModels.map((t) => t.toJSON());

      // Filter out annotations and translations if we're not maintaining a full selection of a circular sequence.
      if (!keepFullCircular) {
        const reindexedEnd = sequtil.getReindexedPosition(end, start, seqLength);
        annotations = this.filterAndClampAnnotations(options.notcontained, annotations, reindexedEnd);
        translations = this.handleTranslationsFromIndices(options.notcontained, translations, start, end);
      } else {
        translations = this.reindexFeatures(translations, start);
      }
    }

    let newPrimers: Array<SequencePrimerAttributes> = [];
    if (options.annotations) {
      // Look only at primers within the selection, not counting the overhang
      const primersToPreserve = this.sequencePrimers().filter(
        // Primers at the end of the sequence have an end of 0, but the selection end doesn't follow this
        // pattern (i.e. it will not be 0 if it goes to the end of the sequence).
        // This means we have to set the selection end to 0 if it equals the sequence length.
        (p) => {
          if (shouldRejectSubPartFeature(p)) {
            return false;
          }
          const selectionEnd = end === seqLength ? 0 : end;
          return sequtil.isRangeWithin(p.start(), p.end(), start, selectionEnd) && p.id != null;
        }
      );
      for (const primer of primersToPreserve) {
        const newPrimer = util.cloneWrappableFeature(primer, start, end, seqLength);
        newPrimers.push(newPrimer.toJSON());
      }
    }

    if (linearizeForPrimers) {
      if (startPrimer != null) {
        // When there is a start primer and the include primer bases option is selected,
        // account for the primer's overhang on the left and adjust all features accordingly.
        const padLength = startPrimer.overhangBases().length;
        const selectionLength = sequtil.length(start, end, seqLength);
        this.padFeaturesAtStart(annotations, padLength, selectionLength);
        this.padFeaturesAtStart(translations, padLength, selectionLength);
        this.padFeaturesAtStart(newPrimers, padLength, selectionLength);
        this.padFeaturesAtStart(partAttributes, padLength, selectionLength);
      }

      // Generate new parts based on primers.
      newBases = this.getBasesWithPrimerBases(startPrimer, endPrimer, start, end);

      if (endPrimer != null) {
        const newLength = newBases.length;
        this.fixFeatureEnds(annotations, newLength);
        this.fixFeatureEnds(translations, newLength);
        this.fixFeatureEnds(newPrimers, newLength);
      }
    } else {
      newBases = sequtil.residueSlice(this.bases(), start, end, true);
    }

    if (options.reverseComplement) {
      newBases = util.revcomp(newBases, this.baseTable());
      util.revcompFeatures(newBases.length, annotations);
      util.revcompFeatures(newBases.length, translations);
      util.revcompFeatures(newBases.length, newPrimers);
      util.revcompParts(newBases.length, partAttributes);
    }

    const [dispStart, dispEnd] = sequtil.modelRangeToDisplay(start, end, newBases.length, this.startIndex());

    annotations = this.cleanFeatures(annotations, newBases.length);
    translations = this.cleanFeatures(translations, newBases.length);
    newPrimers = this.cleanFeatures(newPrimers, newBases.length);

    const useNewSequenceType = options.type != null && options.type !== this.get('type');
    const newNucleotideType = this.getNewNucleotideType(useNewSequenceType, options.type, options.primers);
    if (useNewSequenceType) {
      newBases = util.sanitize(newBases, newNucleotideType);
    }

    // Create new Backbone feature collections
    const annotationsCollection = new Annotations(annotations, {parse: true});
    const translationsCollection = new Translations(translations, {parse: true});
    const primersCollection = new SequencePrimers(newPrimers, {parse: true});
    // If we have bases overridden, we can't be confident they'll match the parts on the existing sequence.
    const partsCollection =
      options.bases == null && !useNewSequenceType
        ? new PartModels.Collection(partAttributes)
        : new PartModels.Collection();
    const translationGroupIds = new Set(translations.map((translation) => translation.translationGroupId));
    const translationGroups = this.translationGroups().filter((translationGroup) =>
      translationGroupIds.has(translationGroup.id)
    );

    if (options.bases) {
      newBases = options.bases;
    }

    let tags: SequenceAttributes['tags'] = [];
    if (options.tags) {
      tags = this.get('tags')?.filter((tag: Tag) => !tagIsComputed(tag));
    }

    const startIndex = options.preserveIndices && !keepFullCircular ? this.startIndex() + start : 1;

    // Use the constructor so that we create
    // a sequence of the same type as the current one! (like gap sequences)
    // @ts-expect-error Use constructor even though TS doesn't like it
    const newSequence = new this.constructor({
      name: options.name ?? `${this.name()} [${dispStart}-${dispEnd}]`,
      bases: newBases,
      parts: partsCollection,
      type: options.type || this.get('type'),
      nucleotideType: newNucleotideType,
      sequencePrimers: primersCollection,
      circular: keepFullCircular,
      annotations: annotationsCollection,
      translations: translationsCollection,
      translationGroups,
      tags,
      startIndex,
      parentVersionId,
      modifiedNucleotides,
    });

    // Set overhang of cloned start and end primers to 0
    if (linearizeForPrimers) {
      for (const primer of newSequence.sequencePrimers().collect()) {
        if ((startPrimer && primer.id === startPrimer.id) || (endPrimer && primer.id === endPrimer.id)) {
          primer.setOverhang(newSequence, 0);
        }
      }
    }

    return newSequence;
  }

  replaceBases(start: number, end: number, newBases: string): number {
    const pos = sequtil.isWrap(start, end) ? 0 : start;
    this.suppressModify(function (this: Sequence): void {
      SequenceEdits.replaceWithBases(this, start, end, newBases);
    });
    return pos;
  }

  suppressModify(f: (seq: Sequence) => void): void {
    // This ensure that base changes occur first, and THEN annotations, translations, and primers follow.
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return Backbone.batchModify<Backbone.Model, Sequence>(
      [this.annotations(), this.translations(), this.sequencePrimers()],
      this,
      function (this: Sequence): Sequence | void {
        context.using({modifying: true}, this, f);
        if (!context.get('modifying')) {
          return this.triggerBaseChange();
        }
      }
    );
  }

  clone(): Sequence {
    // FIXME: write a real clone method
    const clone: Sequence = this.slice().set({
      name: this.get('name'),
      circular: this.get('circular'),
    });
    clone.annotations().reset(this.annotations().deepClone().models);
    clone.translations().reset(this.translations().deepClone().models);
    return clone;
  }

  cloneReverseComplement(): Sequence {
    const clone: Sequence = this.slice().set({
      name: this.get('name'),
      circular: this.get('circular'),
      offset5: this.get('offset3'),
      offset3: this.get('offset5'),
    });
    clone.set({bases: util.revcomp(clone.bases(), this.baseTable())});
    const {length} = this.bases();

    const annotations = this.annotations().deepClone().models;
    annotations.forEach((ann) => {
      const start = ann.start();
      const end = ann.end();
      const strand = ann.strand();
      return ann.set({
        start: (length - end) % length,
        end: (length - start) % length,
        strand: OPPOSITE_STRAND[strand],
      });
    });
    clone.get('annotations').reset(annotations);

    const translations = this.translations().deepClone().models;
    translations.forEach((t) => {
      const start = t.start();
      const end = t.end();
      const strand = t.strand();
      return t.set({
        start: (length - end) % length,
        end: (length - start) % length,
        strand: OPPOSITE_STRAND_OF_DOUBLE_STRANDED[strand],
      });
    });
    clone.translations().reset(translations);
    return clone;
  }

  residueSlice(start: number, end: number, allowEmpty?: boolean): string {
    if (allowEmpty == null) {
      allowEmpty = false;
    }
    if (start === end && allowEmpty) {
      return '';
    }
    return sequtil.residueSlice(this.bases(), start, end, this.circular());
  }

  slice(start?: number, end?: number, revcomp?: boolean): Sequence {
    // TODO: Add in some nicer slicing syntax for circular / negative indicies.

    if (start == null) {
      start = 0;
    }
    if (revcomp == null) {
      revcomp = false;
    }
    if (end === undefined) {
      end = this.length();
    }

    // should this assert be more strict?
    assert(
      start >= 0 && end <= this.length(),
      `cannot slice out of bounds: start ${start}, end ${end}, length=${this.length()}`
    );

    const newbases = revcomp
      ? util.revcomp(sequtil.residueSlice(this.bases(), start, end, true), this.baseTable())
      : sequtil.residueSlice(this.bases(), start, end, true);
    const length = newbases.length;

    function revcompFeature(feature: PolymerFeatureModel): void {
      start = feature.start();
      end = feature.end();
      const strand = feature.strand();
      feature.set({start: length - end, end: (length - start) % length, strand: OPPOSITE_STRAND[strand]});
    }

    const newPartAttrs = this.parts()
      .getSequencePartsContainedWithin(this.length(), start, end)
      .map((p) => util.clonePartAttributes(p.attributes, start!, end!, this.length()));
    if (revcomp) {
      util.revcompParts(length, newPartAttrs);
    }
    const newparts = new Parts(newPartAttrs);

    const newAnnotations = new Annotations();
    for (const ann of Array.from(this.annotations().filter((a) => !a.fromSubPart()))) {
      if (sequtil.isRangeWithin(ann.start(), ann.end(), start, end)) {
        newAnnotations.add(util.cloneWrappableFeature(ann, start, end, this.length()));
        if (revcomp) {
          revcompFeature(ann);
        }
      }
    }

    const newTranslations = new Translations();
    for (const t of Array.from(this.translations().filter((translation) => !translation.fromSubPart))) {
      if (sequtil.isRangeWithin(t.start(), t.end(), start, end)) {
        newTranslations.add(util.cloneWrappableFeature(t, start, end, this.length()));
        if (revcomp) {
          revcompFeature(t);
        }
      }
    }

    const newPrimers = new SequencePrimers();
    for (const primer of Array.from(this.sequencePrimers().filter((p) => !p.fromSubPart()))) {
      if (sequtil.isRangeWithin(primer.start(), primer.end(), start, end)) {
        newPrimers.add(util.cloneWrappableFeature(primer, start, end, this.length()));
        if (revcomp) {
          revcompFeature(primer);
        }
      }
    }

    const offset5 = Math.max(Math.abs(this.get('offset5')) - start, 0) * util.sign(this.get('offset5'));
    const offset3 =
      Math.max(Math.abs(this.get('offset3')) - (this.length() - end), 0) * util.sign(this.get('offset3'));

    // @ts-expect-error Use constructor even though TS doesn't like it
    const seq: Sequence = new this.constructor({
      bases: newbases,
      parts: newparts,
      type: this.get('type'),
      nucleotideType: this.nucleotideType(),
      circular: false,
      annotations: newAnnotations,
      translations: newTranslations,
      sequencePrimers: newPrimers,
      offset5,
      offset3,
    });

    seq.annotations().add(seq.parts().annotations(seq));
    seq.translations().add(seq.parts().translations(seq));
    seq.sequencePrimers().add(seq.parts().sequencePrimers(seq));

    return seq;
  }

  async createTranslation(start: number, end: number, strand: Strand): Promise<Translation> {
    const translation: Translation | false = await this.translations().betterCreate(
      {start, end, strand},
      {fileUpdate: true}
    );
    assert(translation !== false, 'Unexpected validation failure on translation creation.');
    return translation;
  }

  async createTranslationIfNew(start: number, end: number, strand: Strand): Promise<void> {
    for (const translation of this.translations().models) {
      if (translation.start() === start && translation.end() === end && translation.strand() === strand) {
        return;
      }
    }
    await this.createTranslation(start, end, strand);
  }

  isRNASequence(): boolean {
    return this.get('type') === SequenceType.SINGLE_STRANDED;
  }

  isOligo(): boolean {
    return this.get('type') === SequenceType.OLIGO;
  }

  validate(): string | undefined {
    if (this.get('name') == null || this.get('name').length === 0) {
      return 'name cannot be empty';
    }
    if (this.get('type') === 'ss') {
      if (this.nucleotideType() && this.nucleotideType() !== NucleotideType.RNA) {
        return 'ss sequences must be RNA';
      }
      if (this.get('offset5') !== 0 || this.get('offset3') !== 0) {
        return 'ss sequences cannot have overhangs';
      } else if (this.get('circular')) {
        return 'ss sequences cannot be circular';
      }
    } else if (this.get('type') === 'ds') {
      if (this.nucleotideType() && this.nucleotideType() !== NucleotideType.DNA) {
        return 'ds sequences must be DNA';
      }
      if (Math.abs(this.get('offset5')) + Math.abs(this.get('offset3')) > this.length()) {
        return 'overhang cannot be longer than sequence';
      }
    } else if (this.get('type') === 'oligo') {
      if (this.get('offset5') !== 0 || this.get('offset3') !== 0) {
        return 'oligos cannot have overhangs';
      }
    } else {
      return 'incorrect strand type specified';
    }
    return undefined;
  }

  isEmpty(): boolean {
    return this.bases() === '';
  }

  equal(o: Sequence): boolean {
    // TODO: if ds gets cut to a single strand, offset3 and offset5 can be switched.
    return (
      this.get('type') === o.get('type') &&
      util.normalize(this.bases()) === util.normalize(o.bases()) &&
      this.get('offset3') === o.get('offset3') &&
      this.get('offset5') === o.get('offset5') &&
      this.get('circular') === o.get('circular')
    );
  }

  toSaveable(): Sequence {
    return this;
  }

  displayNames(): string {
    const names = [this.get('displayLabel')];
    if (this.get('tagSchema')?.includeRegistryIdInChips) {
      const bioentityAliasInfos: SequenceAttributes['bioentityAliasInfos'] = this.get('bioentityAliasInfos');
      const registryIds =
        bioentityAliasInfos != null
          ? bioentityAliasInfos
              .filter((bioentityAliasInfo) => bioentityAliasInfo.registryCollectionOwner != null)
              .map((aliasInfo) => aliasInfo.alias)
          : [];
      names.push(...registryIds);
    }
    return names.join(', ');
  }
}
Sequence.initClass();

export class SequenceCollection extends Backbone.Collection<Sequence> {
  static initClass(): void {
    this.prototype.model = Sequence;
  }

  equal(o: SequenceCollection): boolean {
    if (this.length !== o.length) {
      return false;
    }

    for (const seq of Array.from(this.models)) {
      if (o.filter((oeq: Sequence) => seq.equal(oeq)).length !== 1) {
        return false;
      }
    }

    return true;
  }
}
SequenceCollection.initClass();

export {Sequence as Model, SequenceCollection as Collection};

function __guard__<T, R>(value: T, transform: (val: T) => R): R | undefined {
  return value != null ? transform(value) : undefined;
}
